import { Button } from "@/components/ui/Button/Button";
import { InputForm } from "@/components/ui/Input/InputForm";
import { Pages } from "@/utils/routes.utils";
import { useResponsive } from "@/utils/useResponsive"
import { useNavigate } from "react-router-dom";
import { AuthStore } from "@/store/auth/auth.store";
import { useResendPin, useSignin } from "@/store/auth/auth.service";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormEvent, Suspense, useEffect } from "react";
import { useForm } from "react-hook-form";
import { pinSchema } from "@/store/auth/auth.model";
import { useTranslation } from "react-i18next";
import { getCustomComponent } from "@/utils/custom-components.utils";
import { useBrandConfig } from "@/utils/useBrandConfig";

export const PinScreen = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { email, loading, setLoading } = AuthStore();
  const { name } = useBrandConfig();

  useEffect(() => {
    if (!email) {
      navigate(Pages.SINGIN);
    }
  }, [email, navigate])

  const { mutate: resendPin } = useResendPin({
    onSuccess: () => {
      toast.success(t('auth.pin.pinResent'));
    },
    onError: (message) => {
      toast.error(message);
    }
  })

  const { mutate: signin } = useSignin({
    onSuccess: () => {
      setLoading(false);
      navigate(Pages.HOME);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const {
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(pinSchema),
    mode: 'onChange',
    defaultValues: {
      pin: '',
    },
  })

  const formValues = watch();

  useEffect(() => {
    if (formValues.pin) {
      trigger('pin');
    }
  }, [formValues.pin, trigger])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trigger();
    if (isValid) {
      setLoading(true);
      signin({
        email: email ?? '',
        pin: formValues.pin,
      });
    }
  }

  const handleBack = () => {
    setLoading(false);
    navigate(Pages.SINGIN);
  }

  const handleResendPin = () => {
    resendPin(email ?? '');
  }

  const CustomLogo = getCustomComponent({
    brandName: name,
    folder: 'BrandLogo',
  })

  const Content = (
    <form
      onSubmit={handleSubmit}
      className={`py-7 flex h-full flex-col justify-start items-center ${isMobile ? 'px-7' : 'w-[360px]'}`}>
      <div className='flex flex-col flex-1 justify-between items-center w-full'>
        <Suspense fallback={<div className="h-[42px]"></div>}>
          <CustomLogo />
        </Suspense>
        <div className={`flex flex-col justify-center items-center w-full ${isMobile ? '' : 'pt-[50px]'}`}>
          <div className="text-headingXLarge mb-2 text-center">
            {t("auth.pin.title")}
          </div>
          <div className="opacity-50 text-center mb-8">
            {t("auth.pin.subtitle")}
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-start items-center w-full">
        <InputForm
          value={formValues.pin}
          error={errors.pin?.message}
          required
          placeholder={t("inputs.pin.placeholder")}
          className="mt-3"
          type="number"
          handleChange={(value) => setValue('pin', value)}
        />
        <div className="flex flex-row justify-center items-center gap-1 mt-6">
          <div className="text-bodySmall opacity-70 leading-none p-0 m-0">{t("auth.pin.footer")}</div>
          <button type='button' onClick={handleResendPin}>
            <div className="text-labelSmall underline leading-none p-0 m-0">{t("auth.pin.resend")}</div>
          </button>
        </div>
        <div className={`flex gap-4 ${isMobile ? 'flex-col-reverse pt-8' : 'flex-row py-12'}`}>
          <Button
            label={t("buttons.back")}
            variant="Rounded"
            size={isMobile ? "large" : "small"}
            onClick={handleBack}
          />
          <Button
            label={t("buttons.confirm")}
            primary
            variant="Rounded"
            size={isMobile ? "large" : "small"}
            onClick={() => { }}
            type="submit"
            loading={loading}
          />
        </div>
      </div>
    </form>
  );

  return isMobile ? Content : (
    <div className="flex justify-center items-start pt-[100px]">
      {Content}
    </div>
  );
}